<template>
  <main class="staff-page">
    <header class="page-heading">
      <div>
        <div class="page-eyebrow">Обратная связь</div>
        <h1>
          {{ propertyId ? `NPS-оценки · ${propertyName || `Объект #${propertyId}`}` : 'Опросы' }}
        </h1>
      </div>
      <Button v-if="propertyId" as-child variant="outline" size="sm">
        <router-link :to="{ name: 'surveys' }">Все объекты</router-link>
      </Button>
    </header>
    <Card>
      <Tabs v-model="mainTab">
        <div class="px-5 pt-5">
          <TabsList>
            <TabsTrigger value="list">Список</TabsTrigger>
            <TabsTrigger value="dynamics">Динамика</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="list">
          <div class="table-toolbar">
            <div class="segmented-control" role="group" aria-label="Период опросов">
              <Button
                v-for="period in surveyPeriods"
                :key="period.value"
                size="sm"
                :variant="dateFilter === period.value ? 'secondary' : 'ghost'"
                :aria-pressed="dateFilter === period.value"
                @click="
                  dateFilter = period.value;
                  onDateFilterChange();
                "
              >
                {{ period.label }}
              </Button>
            </div>
            <label class="ui-field table-search">
              <span class="sr-only">Поиск по объекту или ФИО</span>
              <Input v-model="search" type="search" placeholder="Поиск по объекту или ФИО" />
            </label>
          </div>
          <p v-if="errorMessage" role="alert" class="ui-alert ui-alert--error mx-5 mb-5">
            {{ errorMessage }}
          </p>
          <template v-else>
            <div v-if="loading" class="ui-empty">
              <Spinner />
              <span>Загрузка опросов…</span>
            </div>
            <div v-else class="ui-table-scroll">
              <table class="ui-table survey-table">
                <thead>
                  <tr>
                    <th class="num">
                      <Button
                        variant="ghost"
                        size="sm"
                        class="table-sort"
                        @click="sortSurveys('date')"
                      >
                        Дата
                        <ArrowDownUp :size="13" />
                      </Button>
                    </th>
                    <th>Объект</th>
                    <th>Должность</th>
                    <th>ФИО</th>
                    <th class="num">
                      <Button
                        variant="ghost"
                        size="sm"
                        class="table-sort"
                        @click="sortSurveys('vote')"
                      >
                        Оценка
                        <ArrowDownUp :size="13" />
                      </Button>
                    </th>
                    <th><span class="sr-only">Комментарий</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in surveys"
                    :key="item.id"
                    class="survey-row"
                    tabindex="0"
                    :aria-label="'Отзыв: ' + item.contact_name"
                    @click="openSurveyDrawer($event, { item })"
                    @keydown.enter="openSurveyDrawer($event, { item })"
                    @keydown.space.prevent="openSurveyDrawer($event, { item })"
                  >
                    <td class="num whitespace-nowrap">{{ formatDateTime(item.date) }}</td>
                    <td class="font-medium">{{ item.property_name }}</td>
                    <td>{{ item.position }}</td>
                    <td>{{ item.contact_name }}</td>
                    <td class="num">
                      <span class="score-cell">
                        <span class="mark">
                          <MessageSquare
                            v-if="item.notes"
                            :size="14"
                            title="Есть отзыв"
                            class="text-muted-foreground"
                          />
                        </span>
                        <Badge
                          :variant="
                            item.vote >= 9
                              ? 'success'
                              : item.vote >= 7
                                ? 'secondary'
                                : item.vote >= 5
                                  ? 'warning'
                                  : 'destructive'
                          "
                        >
                          {{ item.vote }}
                        </Badge>
                      </span>
                    </td>
                    <td>
                      <span class="mark">
                        <NotebookPen
                          v-if="item.comments"
                          :size="15"
                          title="Есть комментарий менеджера"
                          class="text-primary"
                        />
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!surveys.length">
                    <td colspan="6" class="text-center text-muted-foreground py-8">
                      Опросы не найдены
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="table-pagination">
              <label class="flex items-center gap-2 text-xs text-muted-foreground">
                Строк
                <select
                  v-model.number="itemsPerPage"
                  class="ui-select"
                  aria-label="Строк на странице"
                  @change="
                    currentPage = 1;
                    loadSurveys();
                  "
                >
                  <option v-for="size in [25, 50, 75, 100, -1]" :key="size" :value="size">
                    {{ size === -1 ? 'Все' : size }}
                  </option>
                </select>
              </label>
              <span>{{ totalCount }} отзывов</span>
              <Button
                variant="outline"
                size="icon"
                aria-label="Предыдущая страница"
                :disabled="currentPage === 1 || loading"
                @click="
                  currentPage--;
                  loadSurveys();
                "
              >
                <ChevronLeft :size="16" />
              </Button>
              <span>{{ currentPage }} / {{ surveyPageCount }}</span>
              <Button
                variant="outline"
                size="icon"
                aria-label="Следующая страница"
                :disabled="currentPage >= surveyPageCount || loading"
                @click="
                  currentPage++;
                  loadSurveys();
                "
              >
                <ChevronRight :size="16" />
              </Button>
            </div>
          </template>
        </TabsContent>
        <TabsContent value="dynamics" class="p-5">
          <SurveyChart
            v-if="propertyFilterValid && !errorMessage"
            :key="propertyId || 'all'"
            :property-id="propertyId"
            :title="propertyId ? 'Динамика оценок объекта' : 'Динамика оценок по всем объектам'"
          />
        </TabsContent>
      </Tabs>
    </Card>
    <SurveyDetailDrawer
      v-model="drawerOpen"
      :survey="selectedSurvey"
      @survey-updated="loadSurveys"
    />
  </main>
</template>

<script setup lang="ts">
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowDownUp, MessageSquare, NotebookPen, ChevronLeft, ChevronRight } from '@lucide/vue';

import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { dateTimeToDateWithTimeString } from '@/filters/date_filters';
import SurveyChart from '@/components/SurveyChart.vue';
import SurveyDetailDrawer from '@/components/SurveyDetailDrawer.vue';

const route = useRoute();
const propertyName = ref('');
const propertyFilterValid = computed(() => {
  const value = route.query.property_id;
  return (
    value === undefined ||
    (typeof value === 'string' &&
      /^\d+$/.test(value) &&
      Number.isSafeInteger(Number(value)) &&
      Number(value) > 0)
  );
});
const propertyId = computed(() =>
  propertyFilterValid.value && route.query.property_id !== undefined
    ? Number(route.query.property_id)
    : null
);

interface Survey {
  id: number;
  date: string;
  property_name: string;
  property_id?: number;
  position: string;
  contact_name: string;
  contact_person_id?: number;
  vote: number;
  notes: string;
  comments: string;
  ip: string | null;
}

const loading = ref(true);
const search = ref('');
const surveys = ref<Survey[]>([]);
const totalCount = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(50);
const sortBy = ref([{ key: 'date', order: 'desc' as 'asc' | 'desc' }]);
const dateFilter = ref('all_time');
const drawerOpen = ref(false);
const selectedSurvey = ref<Survey | null>(null);
const mainTab = ref('list');
const errorMessage = ref<string | null>(null);

let searchTimeout: number | null = null;
let latestRequest = 0;

async function loadPropertyName() {
  const id = propertyId.value;
  propertyName.value = '';
  if (!id) return;
  try {
    const response = await axios.get(`/api/properties/${id}/`);
    if (propertyId.value === id) propertyName.value = response.data.name;
  } catch {
    // The survey request reports access and loading errors; keep the object ID as a fallback.
  }
}

function formatDateTime(dateTime: string): string {
  return dateTimeToDateWithTimeString(dateTime) || '';
}

function debouncedSearch() {
  if (searchTimeout !== null) {
    clearTimeout(searchTimeout);
  }
  searchTimeout = window.setTimeout(() => {
    currentPage.value = 1;
    loadSurveys();
  }, 500);
}

function onDateFilterChange() {
  currentPage.value = 1;
  loadSurveys();
}

async function loadSurveys() {
  const requestId = ++latestRequest;
  loading.value = true;
  errorMessage.value = null;
  if (!propertyFilterValid.value) {
    errorMessage.value = 'Некорректный объект в ссылке на NPS-оценки.';
    surveys.value = [];
    totalCount.value = 0;
    loading.value = false;
    return;
  }
  try {
    const params: any = {
      limit: itemsPerPage.value === -1 ? 10000 : itemsPerPage.value,
      offset: itemsPerPage.value === -1 ? 0 : (currentPage.value - 1) * itemsPerPage.value,
    };

    if (propertyId.value) {
      params.property_id = propertyId.value;
    }

    if (search.value) {
      params.search = search.value;
    }

    // Добавляем фильтр по дате
    if (dateFilter.value && dateFilter.value !== 'all_time') {
      params.date_filter = dateFilter.value;
    }

    // Добавляем сортировку
    if (sortBy.value && sortBy.value.length > 0) {
      const sortItem = sortBy.value[0] as { key: string; order: 'asc' | 'desc' };
      const ordering = sortItem.order === 'desc' ? '-' : '';
      params.ordering = `${ordering}${sortItem.key}`;
    }

    const response = await axios.get('/api/surveys/', { params });
    if (requestId !== latestRequest) return;
    const data = response.data as PagedList<Survey>;
    surveys.value = data.results as Survey[];
    totalCount.value = data.count;
  } catch (e: any) {
    if (requestId !== latestRequest) return;
    if (e.response?.status === 403) {
      // User doesn't have permission
      errorMessage.value =
        'У вас нет прав для просмотра списка опросов. Обратитесь к администратору для получения доступа.';
      surveys.value = [];
      totalCount.value = 0;
    } else if (e.response?.status === 401) {
      errorMessage.value = 'Сессия истекла. Пожалуйста, войдите снова.';
      surveys.value = [];
      totalCount.value = 0;
    } else {
      errorMessage.value = 'Ошибка при загрузке данных. Пожалуйста, попробуйте позже.';
      console.error('Error loading surveys:', e);
      surveys.value = [];
      totalCount.value = 0;
    }
  } finally {
    if (requestId === latestRequest) loading.value = false;
  }
}

async function openSurveyDrawer(event: any, { item }: { item: Survey }) {
  surveyTrigger.value = event.currentTarget as HTMLElement;
  selectedSurvey.value = item;
  drawerOpen.value = true;
}

const surveyTrigger = ref<HTMLElement | null>(null);
watch(drawerOpen, (open) => {
  if (!open) requestAnimationFrame(() => surveyTrigger.value?.focus());
});

watch(
  () => route.query.property_id,
  () => {
    currentPage.value = 1;
    drawerOpen.value = false;
    selectedSurvey.value = null;
    surveys.value = [];
    totalCount.value = 0;
    loadPropertyName();
    loadSurveys();
  }
);

watch(
  () => search.value,
  () => {
    currentPage.value = 1;
    debouncedSearch();
  }
);

onMounted(async () => {
  loadPropertyName();
  await loadSurveys();
});

onBeforeUnmount(() => {
  latestRequest++;
  if (searchTimeout !== null) clearTimeout(searchTimeout);
});

const surveyPeriods = [
  { value: 'all_time', label: 'За всё время' },
  { value: 'last_6_months', label: 'Последние полгода' },
  { value: 'last_3_months', label: 'Последние 3 месяца' },
];
const surveyPageCount = computed(() =>
  itemsPerPage.value === -1 ? 1 : Math.max(1, Math.ceil(totalCount.value / itemsPerPage.value))
);
function sortSurveys(key: string) {
  sortBy.value = [
    {
      key,
      order: sortBy.value[0]?.key === key && sortBy.value[0]?.order === 'asc' ? 'desc' : 'asc',
    },
  ];
  currentPage.value = 1;
  loadSurveys();
}
</script>
